import type { PropertyValues } from 'lit';
import { css, html, nothing } from 'lit';

import { customElement, property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { iconStyles } from 'src/library/icon-styles.js';
import { entities } from 'src/models/search.js';
import { t } from 'src/i18n';
import '../../library/editors/elements/d-edit-text.js';
import '../../library/fields/d-closer.js';
import '../../library/fields/d-expansion.js';
import { headerViewStyles } from 'src/layout/parts/header-view-styles.js';
import { ResponsiveElement } from 'src/library/elements/responsive-container.js';
import type { SearchResult } from './search-result';
import type { DEditText } from 'src/library/editors/elements/d-edit-text';

/**
 *
 *
 */
@customElement('d-organization-search')
export class DOrganizationSearch extends ResponsiveElement {
  static readonly styles = [
    iconStyles,
    headerViewStyles,
    css`
      :host {
        display: block;
      }

      .wrapper {
        min-height: 50px;
      }

      :host(.width600) .wrapper {
        min-height: unset;
      }

      .header {
        display: flex;
        position: relative;
        box-sizing: border-box;
        margin-top: 0;
        margin-bottom: 6px;
        padding: 0 28px 0 0;
        max-width: 1004px;
      }

      d-edit-text {
        flex: 1;
      }

      d-closer {
        top: 0;
        right: -8px;
      }

      .comment.noResults {
        padding: 0 10px 10px 38px;
        margin-top: 6px;
        font-family: var(--mainSans);
        font-weight: 200;
        line-height: 140%;
        font-style: italic;
      }

      .search-result-item {
        padding: 8px 0 7px 28px;
        background-size: 24px 24px;
        background-position: 0 7px;
        background-repeat: no-repeat;
        text-decoration: none;
        display: block;
        margin: 0 10px;
        font-size: 15px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.2);
        color: black;
        opacity: 0.7;
      }

      @media (hover: hover) {
        .search-result-item:hover,
        .search-result-item:active {
          background-color: hsla(0, 0%, 100%, 0.3);
          opacity: 1;
        }
      }

      .listWrapper {
        border-top: 1px solid rgba(0, 0, 0, 0.07);
        margin-top: 10px;
        margin-bottom: 6px;
      }

      .meta-content-wrapper {
        max-width: 1004px;
        max-height: calc((var(--vh, 1vh) * 100) - var(--applicationHeaderHeight) - 600px);
        margin-top: 8px;
        background: hsl(195 65% 70%);
        overflow-y: auto;
      }

      :host(.width600) .meta-content-wrapper {
        width: 1004px;
        max-width: calc(100vw - 40px);
        margin-top: 12px;
        margin-bottom: 0;
      }

      @media only screen and (max-height: 600px) {
        .meta-content-wrapper {
          max-height: calc((var(--vh, 1vh) * 100) - var(--applicationHeaderHeight) - 180px);
          margin-top: 8px;
          margin-bottom: 12px;
        }
      }
    `,
  ];
  @property({ type: Boolean, reflect: true })
  open = false;
  @query('#searchBox')
  searchBox!: DEditText;
  /**
   * The search query
   */
  @property({ type: String })
  query = '';
  /**
   * Number of results to display.
   */
  @property({ type: Number })
  limit = 999;
  /**
   * The search results to display
   */
  @property({ type: Array })
  results: SearchResult[] = [];
  @property({ type: Boolean })
  offlineSearch = false;

  private get items(): SearchResult[] {
    return this.results.slice(0, this.limit);
  }

  _close() {
    this.dispatchEvent(new CustomEvent('close-search'));
  }
  fireQueryValueChanged(e: CustomEvent<{ value: string }>) {
    this.dispatchEvent(
      new CustomEvent<{ query: string }>('search-query-changed', {
        bubbles: true,
        composed: true,
        detail: { query: e.detail.value },
      }),
    );
  }

  render() {
    return html`
      <d-expansion ?opened=${this.open}>
        <div class="wrapper">
          <div class="header">
            <d-edit-text
              type="search"
              outskirts
              id="searchBox"
              placeholder="${t('layout.organizationSearch.placeholder')}"
              .value="${this.query}"
              @value-changed=${this.fireQueryValueChanged}
            ></d-edit-text>
            <d-closer color="white" @click=${() => this._close()}></d-closer>
          </div>
          ${!this.items.length && this.query.trim().length < 3
            ? nothing
            : html`<div class="meta-content-wrapper">
                ${this.items.map(
                  (result) =>
                    html` <a
                      class="search-result-item ${entities[result.entity]}"
                      href="${this._href(result)}"
                      target="${ifDefined(result.target === '_self' ? undefined : result.target)}"
                      data-id="${result.id}"
                      @click=${(e: Event) => this._setStartTask(e, result)}
                    >
                      ${result.description} ${result.party}
                    </a>`,
                )}
              </div>`}
          ${(this.offlineSearch || !navigator.onLine) && this.query.trim().length >= 3
            ? html`<div class="comment noResults">${t('layout.organizationSearch.offlineSearch')}</div>`
            : nothing}
          ${this._shouldHaveResultsOrNoQuery(this.items.length, this.query)
            ? nothing
            : html`<div class="comment noResults">...</div>`}
        </div>
      </d-expansion>
    `;
  }

  _setStartTask(e: Event, result: SearchResult) {
    if (result.entity === 'StartTask') {
      e.preventDefault();
      this.dispatchEvent(
        new CustomEvent<{ uuid: string }>('start-task-selected', {
          bubbles: true,
          composed: true,
          detail: { uuid: result.id },
        }),
      );
    }
  }

  _shouldHaveResultsOrNoQuery(length: number, query: string) {
    const q = query ? query.trim() : '';
    return length > 0 || q === '' || (q && q.length < 3);
  }

  _openChange() {
    if (this.searchBox) this.searchBox.setFocus();
  }

  _href(result) {
    if (result.entity === 'StartTask') {
      return '';
    }
    return result.href;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('open') && this.open) {
      requestAnimationFrame(() => {
        this._openChange();
      });
    }
    this.dispatchEvent(
      new CustomEvent<{ query: string }>('search-updated', {
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-organization-search': DOrganizationSearch;
  }
}
